import React, { useContext, useEffect, useMemo } from 'react';
import {
  BrowserRouter as Router,
  Redirect
} from 'react-router-dom';
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import i18n from './i18n';
import GuestRoute from './react-router/GuestRoute';
import PrivateRoute from './react-router/PrivateRoute';
import AnimatedSwitch from './react-router/AnimatedSwitch';
import EnvironmentVariablesContext
  from './contexts/EnvironmentVariablesContext';
import defaultAnalyticsFields from './utils/defaultAnalyticsFields';
import { useTranslation } from 'react-i18next';
import ScreenSaver from "./components/ScreenSaver";
import { ORIENTATION_TYPES } from "./components/EnsureOrientationOnMobile";

const Login = React.lazy(() => import('./pages/Login'));
const PasswordReset = React.lazy(() => import('./pages/PasswordReset'));
const PasswordNew = React.lazy(() => import('./pages/PasswordNew'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));
const JourneySelection = React.lazy(() => import('./pages/JourneySelection'));
const Journey = React.lazy(() => import('./Journey'));

const disableScreenSaverPaths = [
  '/login',
  '/password/reset',
  '/password/new',
  '/change-password',
];

function createErrorBoundary(environmentVariables) {
  if (process.env.NODE_ENV === 'production') {
    Bugsnag.start({
      apiKey: environmentVariables.REACT_APP_BUGSNAG_API_KEY,
      appVersion: process.env.REACT_APP_BUGSNAG_APP_VERSION,
      releaseStage: process.env.REACT_APP_BUGSNAG_RELEASE_STAGE,
      plugins: [new BugsnagPluginReact()],
    });

    return Bugsnag.getPlugin('react').createErrorBoundary(React);
  } else {
    return ({ children }) => children;
  }
}

function App() {
  const { t } = useTranslation();
  const environmentVariables = useContext(EnvironmentVariablesContext);

  const ErrorBoundary = createErrorBoundary(environmentVariables);

  useEffect(function() {
    // noinspection JSIgnoredPromiseFromCall
    i18n.changeLanguage(environmentVariables.REACT_APP_I18N_LANGUAGE)
  }, [environmentVariables.REACT_APP_I18N_LANGUAGE]);

  const routeAnalytics = useMemo(() => ({
    skip: false,
    scopedDimensions: false,
    fields: () => defaultAnalyticsFields(environmentVariables),
  }), [environmentVariables]);

  return (
    <ErrorBoundary>
      <Router>
        <ScreenSaver disabledForPaths={disableScreenSaverPaths}>
          <AnimatedSwitch>
            <GuestRoute
              exact
              path="/login"
              title="Login"
              component={Login}
              analytics={routeAnalytics}
            />

            <GuestRoute
              exact
              path="/password/reset"
              title={t('title.resetPassword')}
              component={PasswordReset}
              analytics={routeAnalytics}
            />

            <GuestRoute
              exact
              path="/password/new"
              title={t('title.newPassword')}
              component={PasswordNew}
              analytics={routeAnalytics}
            />

            <PrivateRoute
              exact
              path="/change-password"
              title={t('title.newPassword')}
              component={ChangePassword}
              analytics={routeAnalytics}
            />

            <PrivateRoute
              exact
              path="/"
              title="Story selection"
              component={JourneySelection}
              analytics={routeAnalytics}
              orientation={ORIENTATION_TYPES.PORTRAIT}
            />

            <PrivateRoute
              path="/:slug"
              component={Journey}
            />

            <Redirect to="/" />
          </AnimatedSwitch>
        </ScreenSaver>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
